/**
 * DeleteConfirmDialog — single-shape confirmation for destructive ops.
 *
 * Used for both single-item delete (1 file) and bulk delete (N files).
 * Per DESIGN.md: title = action verb + count + noun, body = 1 sentence
 * describing what happens + reversibility, primary button uses --error.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

export interface DeleteConfirmDialogProps {
  open: boolean;
  /** Number of items being deleted (1 = single, >1 = bulk). */
  count: number;
  /** Optional name preview for single-item deletes. */
  itemName?: string;
  /** True while the delete is in flight; disables both buttons. */
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DeleteConfirmDialog({
  open, count, itemName, busy, onCancel, onConfirm,
}: DeleteConfirmDialogProps) {
  const title = count === 1
    ? `Delete ${itemName ? `"${itemName}"` : 'this item'}?`
    : `Delete ${count} items?`;
  const body = count === 1
    ? 'This permanently removes it from your bucket. Active shares to this file will return "not found" to recipients.'
    : 'This permanently removes them from your bucket. Active shares to these files will return "not found" to recipients.';

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{body}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={onConfirm} disabled={busy} color="error" variant="contained">
          {busy ? 'Deleting...' : count === 1 ? 'Delete' : `Delete ${count}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
